/**
 * AutoScroll - Automatic scroll system for filters and sections
 *
 * Replaces anchors (#id) in URLs while maintaining the user experience
 * without impacting the SEO crawl budget.
 * To use, add the class `js-auto-scroll` to a container element
 * and specify the target element with the `data-scroll-target` attribute.
 * Optional attributes:
 * - `data-scroll-delay`: Delay before scrolling (default: 100ms)
 * - `data-scroll-behavior`: Scroll behavior (default: 'smooth')
 * - `data-scroll-block`: Vertical alignment (default: 'start')
 */

$(function () {
    /**
     * AutoScroll module for automatically scrolling to target elements on the page.
     *
     * @namespace AutoScroll
     * @property {Object} config - Configuration settings for AutoScroll behavior
     * @property {string} config.selector - CSS selector for auto-scroll containers (default: '.js-auto-scroll')
     * @property {number} config.defaultDelay - Default delay before scrolling in milliseconds (default: 100)
     * @property {string} config.defaultBehavior - Default scroll behavior: 'smooth' or 'auto' (default: 'smooth')
     * @property {string} config.defaultBlock - Default vertical alignment: 'start', 'center', 'end', or 'nearest' (default: 'start')
     * @property {boolean} config.debug - Enable debug logging (default: false)
     *
     * @property {Object} selectors - Data attribute selectors used by the module
     * @property {string} selectors.container - Container element selector
     * @property {string} selectors.dataTarget - Data attribute for target element selector
     * @property {string} selectors.dataDelay - Data attribute for custom scroll delay
     * @property {string} selectors.dataBehavior - Data attribute for custom scroll behavior
     * @property {string} selectors.dataBlock - Data attribute for custom block alignment
     *
     * @property {NodeList} containers - Collection of container elements found in the DOM
     *
     * @method initialize - Initializes the AutoScroll module and triggers scrolling
     * @method findAndScroll - Finds all containers and scrolls to their respective targets
     * @method scrollToTarget - Scrolls to a specific target element based on container configuration
     * @param {HTMLElement} container - The container element with scroll configuration
     * @param {number} index - Index of the container in the collection
     *
     * @method getScrollOptions - Extracts scroll options from container data attributes
     * @param {HTMLElement} container - The container element to extract options from
     * @returns {Object} Object containing delay, behavior, block, and inline scroll options
     *
     * @method log - Logs debug messages when debug mode is enabled
     * @param {...*} args - Arguments to log to the console
     *
     * @method refresh - Re-scans the DOM and re-executes auto-scroll functionality
     *
     * @example
     * // HTML usage:
     * // <div class="js-auto-scroll"
     * //      data-scroll-target="#section-id"
     * //      data-scroll-delay="500"
     * //      data-scroll-behavior="smooth"
     * //      data-scroll-block="center">
     * // </div>
     *
     * // JavaScript usage:
     * window.AutoScroll = AutoScroll;
     * AutoScroll.initialize();
     */
    const AutoScroll = {
        config: {
            selector: '.js-auto-scroll',
            defaultDelay: 100,
            defaultBehavior: 'smooth',
            defaultBlock: 'start',
            debug: process.env.NODE_ENV !== 'production',
        },

        selectors: {
            container: '.js-auto-scroll',
            dataTarget: 'data-scroll-target',
            dataDelay: 'data-scroll-delay',
            dataBehavior: 'data-scroll-behavior',
            dataBlock: 'data-scroll-block',
        },

        containers: [],

        initialize() {
            this.findAndScroll();
        },

        findAndScroll() {
            this.containers = document.querySelectorAll(this.selectors.container);

            if (this.containers.length === 0) {
                this.log('Nenhum container encontrado');
                return;
            }

            this.log(`${this.containers.length} container(s) encontrado(s)`);

            this.containers.forEach((container, index) => {
                this.scrollToTarget(container, index);
            });
        },

        scrollToTarget(container, index) {
            const targetSelector = container.getAttribute(this.selectors.dataTarget);

            if (!targetSelector) {
                console.warn(`AutoScroll: data-scroll-target not defined in container [${index}]`);
                return;
            }

            const targetElement = document.querySelector(targetSelector);

            if (!targetElement) {
                console.warn(`AutoScroll: element not found for "${targetSelector}"`);
                return;
            }

            const scrollOptions = this.getScrollOptions(container);

            this.log(`Scroll to "${targetSelector}"`);

            setTimeout(() => {
                targetElement.scrollIntoView({
                    behavior: scrollOptions.behavior,
                    block: scrollOptions.block,
                    inline: scrollOptions.inline,
                });
            }, scrollOptions.delay);
        },

        getScrollOptions(container) {
            const delay = parseInt(container.getAttribute(this.selectors.dataDelay) || this.config.defaultDelay, 10);
            const behavior = container.getAttribute(this.selectors.dataBehavior) || this.config.defaultBehavior;
            const block = container.getAttribute(this.selectors.dataBlock) || this.config.defaultBlock;

            return {
                delay,
                behavior,
                block,
                inline: 'nearest',
            };
        },

        log(...args) {
            if (this.config.debug) {
                console.log('[AutoScroll]', ...args);
            }
        },

        refresh() {
            this.findAndScroll();
        },
    };

    AutoScroll.initialize();

    window.AutoScroll = AutoScroll;
});
